import { useCallback, useState } from "react";
import { Image, Linking, Pressable, StyleSheet, View } from "react-native";
import CustomText from "../../../common/general/CustomText/CustomText";
import { colors, radii, semanticColors, spacing, webTransitions } from "../../../../styles/design-system";
import YoutubeVideoPlayerAdvanced from "../../../youtube/YoutubeVideoPlayerAdvanced";

export interface VideoCardProps {
  url: string;
  videoId?: string | null;
  title?: string | null;
  thumbnailUrl?: string | null;
}

const PLAY_ICON_SIZE = 22;

export const VideoCard = ({ url, videoId, title, thumbnailUrl }: VideoCardProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const handlePress = useCallback(() => {
    // Facade pattern: click replaces the thumbnail with an inline embedded
    // player. Only videos with a known videoId can embed inline; anything
    // else falls back to opening the URL externally.
    if (videoId) {
      setIsPlaying(true);
      return;
    }
    Linking.openURL(url).catch((err) => {
      console.warn("VideoCard: failed to open URL", url, err);
    });
  }, [url, videoId]);

  if (isPlaying && videoId) {
    return (
      <View style={styles.container}>
        <YoutubeVideoPlayerAdvanced id={videoId} autoplay />
        {title ? (
          <View style={styles.titleBar}>
            <CustomText
              useTranslationText={false}
              text={title}
              size="s"
              weight="semibold"
              style={styles.titleText}
              numberOfLines={2}
            />
          </View>
        ) : null}
      </View>
    );
  }

  const containerStyle = [styles.container, isHovered && styles.containerHover, isPressed && styles.containerPressed];

  return (
    <Pressable
      onPress={handlePress}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      onPressIn={() => setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
      style={containerStyle}
      accessibilityRole="button"
      accessibilityLabel={title ? `Play video: ${title}` : "Play video"}>
      <View style={styles.thumbnailWrapper}>
        {thumbnailUrl ? (
          <Image source={{ uri: thumbnailUrl }} style={styles.thumbnail} resizeMode="cover" />
        ) : (
          <View style={styles.thumbnailPlaceholder} />
        )}
        <View style={[styles.thumbnailScrim, isHovered && styles.thumbnailScrimHover]} />
        <View
          style={[styles.playOverlay, isHovered && styles.playOverlayHover, isPressed && styles.playOverlayPressed]}>
          <View style={styles.playIcon} />
        </View>
      </View>
      {title ? (
        <View style={styles.titleBar}>
          <CustomText
            useTranslationText={false}
            text={title}
            size="s"
            weight="semibold"
            style={styles.titleText}
            numberOfLines={2}
          />
        </View>
      ) : null}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    maxWidth: 720,
    borderRadius: radii.xl,
    overflow: "hidden",
    backgroundColor: semanticColors.surface.white,
    marginTop: spacing.sm,
    marginBottom: spacing.xs,
    borderWidth: 1,
    borderColor: colors.grey[200],
    ...webTransitions.fast,
  },
  containerHover: {
    borderColor: colors.grey[400],
  },
  containerPressed: {
    transform: [{ scale: 0.99 }],
  },
  thumbnailWrapper: {
    width: "100%",
    aspectRatio: 16 / 9,
    backgroundColor: colors.grey[900],
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  thumbnail: {
    ...StyleSheet.absoluteFillObject,
  },
  thumbnailPlaceholder: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: colors.grey[800],
  },
  thumbnailScrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.08)",
    ...webTransitions.fast,
  },
  thumbnailScrimHover: {
    backgroundColor: "rgba(0,0,0,0.18)",
  },
  playOverlay: {
    width: 64,
    height: 64,
    borderRadius: radii.full,
    backgroundColor: "rgba(17, 17, 17, 0.78)",
    justifyContent: "center",
    alignItems: "center",
    ...webTransitions.fast,
  },
  playOverlayHover: {
    backgroundColor: colors.brand[500],
    transform: [{ scale: 1.06 }],
  },
  playOverlayPressed: {
    transform: [{ scale: 0.96 }],
  },
  playIcon: {
    width: 0,
    height: 0,
    borderTopWidth: PLAY_ICON_SIZE / 2,
    borderBottomWidth: PLAY_ICON_SIZE / 2,
    borderLeftWidth: PLAY_ICON_SIZE,
    borderTopColor: "transparent",
    borderBottomColor: "transparent",
    borderLeftColor: colors.white,
    marginLeft: 5,
  },
  titleBar: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
  },
  titleText: {
    color: colors.grey[800],
  },
});
